"use client"

import { useState, useEffect } from "react"
import { createPortal } from "react-dom"
import { JerseyPreview } from "./jersey-preview"
import { ModelSelector } from "./model-selector"
import { ColorPicker } from "./color-picker"
import { TextPicker } from "./text-picker"
import { LogoPicker } from "./logo-picker";
import { UploadLogoMobile } from "./upload-logo-mobile"
import { Button } from "../../../components/ui/button"
import {
  RotateCcw,
  ShoppingCart,
  Palette,
  Hash,
  ImageIcon,
  Shirt,
  Phone,
  HelpCircle,
  PlusCircle,
  Instagram,
  Layers2Icon,
  PaletteIcon,
  BrushCleaningIcon,
  Maximize2,
  Minimize2,
  Save,
  CheckCircle2,
  FolderOpen,
  BoxesIcon,
  User,
} from "lucide-react"
import { cn } from "../../../lib/utils"
import { HelpModal } from "./help-modal"
import { LayersPicker } from "./layers-picker"
import "./simulator-selector"
import { OrderForm } from "../../../components/Sections"
import { ApprovalFlow } from "./approval-flow"
import { getClient, registerProjectIframe, saveIframeProgress } from "../../../API"
import "../../../styles/jerseybird.css"

// ── Tab definition (single source of truth) ──────────────────────────────────
type TabId = "models" | "colors" | "personalize" | "logo" | "layers"

// Tabs that trigger loadMap when first activated
const EDIT_TABS = new Set<TabId>(["colors", "personalize", "logo", "layers"])

const TABS: { id: TabId; icon: React.ReactNode; label: string }[] = [
  { id: "models", icon: <Shirt className="w-4 h-4" />, label: "Modelos" },
  { id: "colors", icon: <Palette className="w-4 h-4" />, label: "Cores" },
  { id: "personalize", icon: <Hash className="w-4 h-4" />, label: "Nome" },
  { id: "logo", icon: <ImageIcon className="w-4 h-4" />, label: "Logo" },
  { id: "layers", icon: <Layers2Icon className="w-4 h-4" />, label: "Estampas" },
]

// ── Shared tab content (rendered in both desktop panel and mobile drawer) ─────
function TabContent({
  activeTab,
  colors,
  onColorChange,
  name,
  number,
  onNameChange,
  onNumberChange,
  logo,
  onLogoChange,
  isMobile = false,
}: {
  activeTab: TabId | null
  colors: { primary: string; secondary: string; accent: string; shorts: string; shortsAccent: string }
  onColorChange: (k: string, v: string) => void
  name: string
  number: string
  onNameChange: (v: string) => void
  onNumberChange: (v: string) => void
  logo: string | null
  onLogoChange: (v: string | null) => void
  isMobile?: boolean
}) {
  // Nothing selected yet — show a prompt
  if (!activeTab) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center">
        <div className="w-12 h-12 rounded-2xl bg-secondary/40 flex items-center justify-center">
          <Palette className="w-6 h-6 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground max-w-[200px]">
          Selecione uma opção acima para personalizar o uniforme
        </p>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
      {/* Models tab — only rendered in mobile drawer to avoid duplicate IDs */}
      {isMobile && (
        <div className={activeTab === "models" ? "flex flex-col gap-4" : "hidden"}>
          <div className="hidden">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <Shirt className="w-3.5 h-3.5" /> Modelagens
            </p>
            <div id="modelagens-iframe-mobile" className="min-h-[60px]" />
          </div>
          <div>
            {/**<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <PaletteIcon className="w-3.5 h-3.5" /> Designs
            </p> */}
            <div id="designs-iframe-mobile" className="min-h-[60px] flex flex-wrap" />
          </div>
        </div>
      )}
      {activeTab === "colors" && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Selecione as cores para personalizar seu uniforme</p>
          <ColorPicker />
        </div>
      )}
      {activeTab === "personalize" && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Adicione nome e número ao uniforme</p>
          <div className="border-t border-border/30 pt-4">
            <TextPicker />
          </div>
        </div>
      )}
      {activeTab === "logo" && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Adicione o escudo ou logo do seu time</p>
          <LogoPicker />
        </div>
      )}
      {activeTab === "layers" && (
        <div className="space-y-4">
          <LayersPicker />
        </div>
      )}
    </div>
  )
}

// ── Shared tab bar ─────────────────────────────────────────────────────────────
// Painel com as artes salvas (localStorage desse navegador) — mostra
// thumbnail + data, clique carrega direto no editor, sem precisar recarregar
// a página.
function DraftsMenu({
  drafts,
  imagesBase,
  onSelect,
  onRemove,
  onClose,
}: {
  drafts: any[]
  imagesBase: string
  onSelect: (draft: any) => void
  onRemove: (id: number) => void
  onClose: () => void
}) {
  if (typeof document === "undefined") return null

  return createPortal(
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 2147483000,
        background: "rgba(0,0,0,.7)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "100%", maxWidth: 320, maxHeight: "70vh", overflowY: "auto",
          background: "#16161a", border: "1px solid rgba(255,255,255,.12)",
          borderRadius: 16, padding: 10, boxShadow: "0 12px 30px rgba(0,0,0,.5)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 6px 10px" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Minhas artes salvas</span>
          <button
            onClick={onClose}
            style={{ width: 26, height: 26, borderRadius: 8, background: "rgba(255,255,255,.08)", color: "#fff", fontSize: 12 }}
          >
            ✕
          </button>
        </div>

        {drafts.length === 0 && (
          <div style={{ fontSize: 12, color: "rgba(255,255,255,.5)", padding: "16px 6px", textAlign: "center" }}>
            Nenhuma arte salva ainda. Clique em "Salvar" pra guardar o que você está personalizando.
          </div>
        )}

        {drafts.map((d) => (
          <div
            key={d.id}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: 8, borderRadius: 10, cursor: "pointer",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,.06)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            onClick={() => onSelect(d)}
          >
            <div style={{ width: 48, height: 48, borderRadius: 8, overflow: "hidden", background: "rgba(255,255,255,.08)", flexShrink: 0 }}>
              {d.thumb && (
                <img src={imagesBase + d.thumb} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>Rascunho #{d.id}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)" }}>
                {new Date(d.savedAt).toLocaleString("pt-BR")}
              </div>
            </div>
            <button
              title="Remover da lista"
              style={{ fontSize: 13, color: "rgba(255,255,255,.4)", padding: 6, flexShrink: 0 }}
              onClick={(e) => { e.stopPropagation(); onRemove(d.id) }}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>,
    document.body
  )
}

function TabBar({
  activeTab,
  onSelect,
  variant = "desktop",
  exclude = [] as TabId[],
}: {
  activeTab: TabId | null
  onSelect: (id: TabId) => void
  variant?: "desktop" | "mobile"
  exclude?: TabId[]
}) {
  const tabs = TABS.filter(t => !exclude.includes(t.id))
  const base = variant === "desktop"
    ? "flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium transition-all rounded-xl"
    : "flex-1 flex items-center justify-center gap-1.5 px-2 py-2 text-sm font-medium transition-all rounded-lg"

  return (
    <div className={cn(
      "flex shrink-0 p-1.5",
      variant === "desktop" ? "bg-secondary/30" : "bg-secondary/30 rounded-xl mb-4"
    )}>
      {tabs.map(t => (
        <button
          key={t.id}
          onClick={() => onSelect(t.id)}
          className={cn(
            base,
            activeTab === t.id
              ? "bg-primary text-primary-foreground shadow-lg"
              : "text-muted-foreground hover:text-foreground hover:bg-white/5"
          )}
        >
          {t.icon}
          <span className={variant === "mobile" ? "hidden xs:inline" : ""}>{t.label}</span>
        </button>
      ))}
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function UniformSimulator() {
  const [activeTab, setActiveTab] = useState<TabId | null>(null)
  const [svgLoaded, setSvgLoaded] = useState(false)
  const [pattern, setPattern] = useState("stripes-vertical")
  const [colors, setColors] = useState({
    primary: "#EF4444", secondary: "#FFFFFF",
    accent: "#1E3A5F", shorts: "#1E3A5F", shortsAccent: "#EF4444",
  })
  const [name, setName] = useState("")
  const [number, setNumber] = useState("10")
  const [logo, setLogo] = useState<string | null>(null)
  const [mobileFullscreen, setMobileFullscreen] = useState(false)
  const [infoOpen, setInfoOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isApprovalOpen, setIsApprovalOpen] = useState(false)
  const [budgetDesign, setBudgetDesign] = useState<any>(null)
  const [sellerWhatsapp, setSellerWhatsapp] = useState<string | null>(null)
  const [companyInfo, setCompanyInfo] = useState<{ name: string | null; email: string | null }>({ name: null, email: null })
  const [savedProgressId, setSavedProgressId] = useState<number | null>(() => {
    if (typeof window === "undefined") return null
    const fromUrl = new URLSearchParams(window.location.search).get("save")
    return fromUrl ? Number(fromUrl) : null
  })
  const [isSavingProgress, setIsSavingProgress] = useState(false)
  const [savedDrafts, setSavedDrafts] = useState<any[]>([])
  const [isDraftsMenuOpen, setIsDraftsMenuOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  // Totem (desktop portrait): which tab the touch drawer is open on (null = closed)
  const [totemDrawerTab, setTotemDrawerTab] = useState<TabId | null>(null)
  // Totem: whether the bottom designs sheet is open. The sheet renders its
  // own #designs-iframe-mobile container (the mobile tree's panel doesn't
  // mount in totem mode and the desktop tree's #iframe-left-panel is hidden),
  // so the existing populators — ksSelectorRefreshMobile (renderDesigns fills
  // every #designs-iframe[-mobile] found) and ksPopulateMobileDesigns
  // (drains _mobileDesignsQueue) — fill it like any mobile drawer.
  const [totemDesignsOpen, setTotemDesignsOpen] = useState(false)

  const toggleTotemDesigns = () => {
    const opening = !totemDesignsOpen
    setTotemDesignsOpen(opening)
    if (!opening) return
    // Populate on open — requestAnimationFrame so the container is committed
    // to the DOM before looking for it. Primary source: window._ksDesigns
    // (pushed by Loads.js for every design, survives React tree swaps — the
    // desktop #designs-iframe is destroyed when the mobile tree mounts on
    // resize). Cards are built with the same attrs (.ks-design-card2,
    // id="project-X", data-sport, data-id) so the delegated body handler in
    // Loads.js ("#designs-iframe-mobile div") picks the clicks up — selection
    // logic untouched. Fallbacks: _mobileDesignsQueue (.ks-design-card2 flow
    // queued at load) and mirroring #designs-iframe (clone + orig.click()).
    requestAnimationFrame(() => {
      if (typeof (window as any).ksSelectorRefreshMobile === "function")
        (window as any).ksSelectorRefreshMobile()
      // renderModels (called by the refresh above) needs #modelagens-iframe-
      // mobile to exist — it's in the sheet above. If it rendered cards, the
      // designs grid was rendered too and we're done with this flow.

      const c = document.getElementById("designs-iframe-mobile")
      if (!c || c.children.length) return

      const designs = (window as any)._ksDesigns as
        { id: string; name: string; img: string; sport: string; index: number }[] | undefined
      if (designs && designs.length) {
        designs.forEach(d => {
          const card = document.createElement("div")
          card.className = "ks-design-card2"
          card.id = d.id
          card.setAttribute("data-sport", d.sport)
          card.setAttribute("data-id", String(d.index))
          const img = document.createElement("img")
          img.src = d.img
          img.alt = d.name
          img.onerror = function () {
            const fb = document.createElement("div")
            fb.className = "ks-design-fallback"
            fb.textContent = "🎨"
            img.replaceWith(fb)
          }
          const name = document.createElement("span")
          name.className = "ks-design-name"
          name.textContent = d.name
          card.appendChild(img)
          card.appendChild(name)
          c.appendChild(card)
        })
        return
      }

      const queue = (window as any)._mobileDesignsQueue as string[] | undefined
      if (queue && queue.length) {
        queue.forEach(el => c.insertAdjacentHTML("beforeend", el))
        return
      }

      const src = document.getElementById("designs-iframe")
      const cards = src ? Array.from(src.children).filter(el => el.tagName === "DIV") : []
      cards.forEach(orig => {
        const clone = orig.cloneNode(false) as HTMLElement
        clone.innerHTML = orig.innerHTML
        clone.addEventListener("click", () => (orig as HTMLElement).click())
        c.appendChild(clone)
      })
    })
  }

  // Drives which of the two completely separate layouts (desktop / mobile)
  // gets mounted below — a real viewport check rather than CSS-only hiding,
  // so the two never coexist in the DOM (avoids duplicate #ids).
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 1024
  )
  useEffect(() => {
    if (typeof window === "undefined") return
    const mq = window.matchMedia("(max-width: 1023px)")
    const update = () => setIsMobile(mq.matches)
    mq.addEventListener("change", update)
    return () => mq.removeEventListener("change", update)
  }, [])

  const [hasARSupport, setHasARSupport] = useState(false)
  const [poseActive, setPoseActive] = useState(false)
  const [hasPoseSupport, setHasPoseSupport] = useState(false)
  // Totem = portrait window on a desktop UA (resized window or vertical
  // monitor kiosk). Phones stay on the regular mobile panel even in portrait.
  const [isTotem, setIsTotem] = useState(false)
  useEffect(() => {
    const nav = navigator as any
    if (nav.xr) {
      nav.xr.isSessionSupported("immersive-ar").then(setHasARSupport).catch(() => {})
    }
    if (typeof navigator !== "undefined" && typeof navigator.mediaDevices?.getUserMedia === "function") {
      setHasPoseSupport(true)
    }
    const onPoseChange = (e: CustomEvent) => setPoseActive(e.detail.active)
    window.addEventListener("pose-tracking:change", onPoseChange as EventListener)

    const isPhoneUA = function () {
      return /(android|iphone|ipad|ipod|mobile|silk)/i.test(navigator.userAgent)
    }
    const checkPortrait = function () {
      setIsTotem(window.innerHeight > window.innerWidth && !isPhoneUA())
    }
    checkPortrait()
    window.addEventListener("resize", checkPortrait)
    window.addEventListener("orientationchange", checkPortrait)
    return () => {
      window.removeEventListener("pose-tracking:change", onPoseChange as EventListener)
      window.removeEventListener("resize", checkPortrait)
      window.removeEventListener("orientationchange", checkPortrait)
    }
  }, [])

  // Totem mode: drive #iframe-left-panel / header visibility via the
  // body.totem-mode class (CSS in SimulatorIframe.scss) so it reacts to
  // window resizes — manageOptions() only runs once at load and misses
  // landscape→portrait. Leaving totem mode clears any inline portrait
  // styles so the panel returns to its normal sidebar layout.
  useEffect(() => {
    if (typeof document === "undefined") return
    document.body.classList.toggle("totem-mode", isTotem)
    if (!isTotem) {
      const el = document.getElementById("iframe-left-panel")
      if (el) el.removeAttribute("style")
    }
  }, [isTotem])

  // Busca o WhatsApp da loja assim que possível — não só quando o Orçamento
  // abre, já que o botão flutuante do WhatsApp (canto direito) também
  // precisa dele desde o início. O modelo 3D pode levar um instante pra
  // carregar, então tenta de novo por alguns segundos até achar o ownerId.
  useEffect(() => {
    if (typeof window === "undefined") return
    let cancelled = false
    let attempts = 0

    const tryFetch = () => {
      if (cancelled) return
      const projectIframe = (window as any).project_iframe || {}
      const ownerId = (window as any).userIDIframe || projectIframe.user_id

      if (ownerId) {
        getClient(ownerId)
          .then((client: any) => {
            if (cancelled) return
            setSellerWhatsapp(client?.[0]?.whatsapp || null)
            setCompanyInfo({ name: client?.[0]?.name || null, email: client?.[0]?.email || null })
          })
          .catch((e: any) => console.error("[index.tsx] erro ao buscar whatsapp da loja:", e))
        return
      }

      attempts += 1
      if (attempts < 20) setTimeout(tryFetch, 500)
    }

    tryFetch()
    return () => { cancelled = true }
  }, [])

  // Desktop hides the customization panel for "view_only" share links via
  // jQuery removing #iframe-right-panel (and, for non-catalog links,
  // #iframe-left-panel too). The mobile panel is React-owned now, so
  // Loads.js dispatches this event instead of reaching for a #mobile-panel
  // element that no longer exists — this mirrors that same rule on mobile.
  const [shareMode, setShareMode] = useState<{
    viewOnly: boolean
    shared: boolean
    isApproval: boolean
    approvalClientName: string
    approvalClientEmail: string
  }>({
    viewOnly: false,
    shared: false,
    isApproval: false,
    approvalClientName: "",
    approvalClientEmail: "",
  })
  useEffect(() => {
    if (typeof window === "undefined") return

    const applyFromDataShared = (source: any) => {
      if (!source) return
      const sharedParam = new URLSearchParams(window.location.search).get("s")
      setShareMode({
        viewOnly: source.mode === "view_only" || source.mode === "approval",
        shared: !!sharedParam,
        isApproval: source.mode === "approval",
        approvalClientName: source.approvalClientName || "",
        approvalClientEmail: source.approvalClientEmail || "",
      })
    }

    const onDataShared = (e: Event) => {
      const detail = (e as CustomEvent).detail || {}
      setShareMode({
        viewOnly: detail.mode === "view_only" || detail.mode === "approval",
        shared: !!detail.shared,
        isApproval: detail.mode === "approval",
        approvalClientName: detail.approvalClientName || "",
        approvalClientEmail: detail.approvalClientEmail || "",
      })
    }
    window.addEventListener("simulatorDataShared", onDataShared)

    // Reforço: manageOptions() roda dentro do carregamento do modelo 3D e
    // pode disparar o evento acima ANTES desse listener existir (é um
    // evento de disparo único, não fica "guardado" pra quem chega depois).
    // Por segurança, também checa window.dataShared diretamente, tentando
    // de novo por alguns segundos até existir.
    let cancelled = false
    let attempts = 0
    const pollDataShared = () => {
      if (cancelled) return
      if ((window as any).dataShared) {
        applyFromDataShared((window as any).dataShared)
        return
      }
      attempts += 1
      if (attempts < 20) setTimeout(pollDataShared, 300)
    }
    pollDataShared()

    return () => {
      cancelled = true
      window.removeEventListener("simulatorDataShared", onDataShared)
    }
  }, [])

  // Detect mobile QR flow — must be after all hooks
  const params = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : ""
  )
  const isMobileUpload = params.get("mobile") === "1" && !!params.get("qr")

  const handleColorChange = (key: string, value: string) =>
    setColors(prev => ({ ...prev, [key]: value }))

  // Called when any tab is selected — loads SVG once on first edit-tab click
  const handleTabSelect = (id: TabId) => {
    if (id === "models") { setActiveTab(id); if (typeof (window as any).ksPopulateMobileDesigns === "function")
          (window as any).ksPopulateMobileDesigns(); return }

    if (EDIT_TABS.has(id) && !svgLoaded) {
      setSvgLoaded(true);
      (window as any)["loadMap"]();
    }

    setActiveTab(id)
  }

  // When the user picks a different layout, the SVG changes — reset so
  // the next tab click reloads the map with the new SVG's colors.
  // On mobile, the panel is always visible (no drawer to reopen), so
  // instead of clearing the tab we re-select "Modelos" and repopulate the
  // grids — otherwise this same event (which also fires once right after
  // the catalog's default project finishes loading) races with the
  // mount-time pre-selection below and wipes it out.
  useEffect(() => {
    const onLayoutChanged = () => {
      setSvgLoaded(false)
      if (isMobile) {
        handleTabSelect("models")
        requestAnimationFrame(() => {
          if (typeof (window as any).ksSelectorRefreshMobile === "function")
            (window as any).ksSelectorRefreshMobile()
        })
      } else {
        setActiveTab(null)
      }
    }
    window.addEventListener("layoutChanged", onLayoutChanged)
    return () => window.removeEventListener("layoutChanged", onLayoutChanged)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile])

  // Mobile: the customization panel lives directly on the initial screen
  // (no tap-to-open drawer), so pre-select "Modelos" once we know we're on
  // mobile and populate the modelagens/designs grids right away.
  useEffect(() => {
    if (!isMobile) return
    handleTabSelect("models")
    requestAnimationFrame(() => {
      if (typeof (window as any).ksSelectorRefreshMobile === "function")
        (window as any).ksSelectorRefreshMobile()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile])

  // The panel (and its #modelagens-iframe-mobile / #designs-iframe-mobile
  // divs) unmounts entirely while in fullscreen, so exiting fullscreen
  // remounts them empty — refresh the grids so they're not blank until a
  // manual re-click. Doesn't touch which tab is selected.
  useEffect(() => {
    if (!isMobile || mobileFullscreen) return
    requestAnimationFrame(() => {
      if (typeof (window as any).ksSelectorRefreshMobile === "function")
        (window as any).ksSelectorRefreshMobile()
      if (activeTab === "models" && typeof (window as any).ksPopulateMobileDesigns === "function")
        (window as any).ksPopulateMobileDesigns()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileFullscreen, isMobile])

  // Re-run the 3D resize whenever the mobile fullscreen toggle changes the
  // available space for #container-iframe
  useEffect(() => {
    requestAnimationFrame(() => {
      if (typeof (window as any).onWindowResize === "function")
        (window as any).onWindowResize()
      window.dispatchEvent(new Event("resize"))
    })
  }, [mobileFullscreen, isMobile])

  // Keep the Three.js renderer/camera in sync with the actual box size of
  // #container-iframe at all times — catches cases a plain "resize" event
  // won't (vh settling, fullscreen toggle, orientation changes). Re-runs on
  // isMobile flips too, since that swaps in a different #container-iframe
  // DOM node (desktop tree vs mobile tree).
  useEffect(() => {
    if (typeof window === "undefined" || typeof ResizeObserver === "undefined") return
    const el = document.getElementById("container-iframe")
    if (!el) return
    const ro = new ResizeObserver(() => {
      if (typeof (window as any).onWindowResize === "function")
        (window as any).onWindowResize()
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [isMobile])

  const handleReset = () => {
    setPattern("stripes-vertical")
    setColors({ primary: "#EF4444", secondary: "#FFFFFF", accent: "#1E3A5F", shorts: "#1E3A5F", shortsAccent: "#EF4444" })
    setName(""); setNumber("10"); setLogo(null)
    setActiveTab(null)
    setSvgLoaded(false)
  }

  function openModalBudget() {
    (window as any)["setupCamera"]();
    setTimeout(() => {
      const dataURL = (window as any).renderer.domElement.toDataURL("image/png")
      const projectIframe = (window as any).project_iframe || {}
      const ownerId = (window as any).userIDIframe || projectIframe.user_id

      setBudgetDesign({
        id_data: projectIframe.id,
        id: projectIframe.id,
        name: $("#kit-iframe-title").text() || projectIframe.name || "Meu Uniforme",
        thumbnail: dataURL,
        sport_id: projectIframe.sport_id,
      })
      setIsModalOpen(true);

      // Busca o WhatsApp da loja já agora, pra estar pronto (em memória) na
      // hora que o cliente terminar de preencher e clicar "Enviar pedido" —
      // window.open só funciona sem ser bloqueado se rodar de forma síncrona
      // dentro do clique, então não dá pra buscar isso só na hora do envio.
      if (ownerId) {
        getClient(ownerId)
          .then((client: any) => setSellerWhatsapp(client?.[0]?.whatsapp || null))
          .catch((e: any) => console.error("[openModalBudget] erro ao buscar whatsapp da loja:", e))
      }
    }, 100)
  }

  // Monta uma mensagem de texto com o resumo do pedido pro WhatsApp
  function buildWhatsAppMessage(result: any, orderId?: number | null) {
    const c = result.contact || {}
    const o = result.order || {}
    const lines = [
      `🛍️ *Novo pedido pelo simulador*`,
      orderId != null ? `*Referência:* #${orderId}` : null,
      ``,
      `*Design:* ${result.design?.name || ""}`,
      `*Nome:* ${c.firstName || ""}`,
      c.phone ? `*Telefone:* ${c.ddi || ""} ${c.phone}` : null,
      c.email ? `*E-mail:* ${c.email}` : null,
      c.address ? `*Endereço:* ${c.address}` : null,
      o.purposes?.length ? `*Finalidade:* ${o.purposes.join(", ")}` : null,
      o.date ? `*Data de entrega:* ${o.date}` : null,
      o.orderList ? `\n*Lista de pedidos:*\n${o.orderList}` : null,
      o.description ? `\n*Descrição:*\n${o.description}` : null,
    ].filter(Boolean)
    return lines.join("\n")
  }

  // Grava o pedido em iframe_projects (arte + dados de contato), reaproveitando
  // a mesma tabela/lógica que já existia antes. Devolve o id gerado (ou null
  // se falhar), pra usar como referência na mensagem do WhatsApp e pra
  // aparecer na tela "Pedidos do Iframe".
  // Gira o modelo 180° pra capturar a vista de trás, tira a foto, e volta
  // pra posição original. Usa window.model/window.renderer, que já são
  // expostos globalmente pelo Loads.js.
  async function captureBackViewDataURL(): Promise<string | null> {
    const model = (window as any).model
    const renderer = (window as any).renderer
    if (!model || !renderer) return null

    const originalY = model.rotation.y
    try {
      model.rotation.y = originalY + Math.PI
      await new Promise((resolve) => setTimeout(resolve, 250))
      return renderer.domElement.toDataURL("image/png")
    } catch (e) {
      console.error("[captureBackViewDataURL] erro ao capturar vista de trás:", e)
      return null
    } finally {
      model.rotation.y = originalY
      await new Promise((resolve) => setTimeout(resolve, 250))
    }
  }

  async function saveIframeProject(result: any): Promise<number | null> {
    try {
      const projectIframe = (window as any).project_iframe || {}
      const ownerId = (window as any).userIDIframe || projectIframe.user_id
      if (!ownerId) { console.warn("[saveIframeProject] sem ownerId, não vai salvar."); return null }

      const c = result.contact || {}
      const o = result.order || {}

      const data2 = {
        name: c.firstName,
        email: c.email,
        phone: c.phone ? `${c.ddi || ""} ${c.phone}`.trim() : "",
        address: c.address,
        comments: o.description,
        list: o.orderList,
        purposes: o.purposes,
        date: o.date,
      }

      const fabricCanvas = (window as any).fabricCanvas
      const svgJson = fabricCanvas ? JSON.stringify(fabricCanvas.toJSON()) : null
      const fileSvg = fabricCanvas ? fabricCanvas.toSVG() : null
      const backThumb = await captureBackViewDataURL()

      const saved = await registerProjectIframe(
        data2,
        ownerId,
        svgJson,
        result.design?.thumbnail,
        fileSvg,
        projectIframe.sport_id,
        {},
        backThumb
      )

      return saved?.id ?? null
    } catch (e) {
      console.error("[saveIframeProject] erro ao salvar pedido:", e)
      return null
    }
  }

  // Chamado pelo OrderForm assim que o cliente clica "Enviar pedido": salva
  // o pedido (com a arte) em iframe_projects e, assim que salvo, já abre o
  // WhatsApp da loja sozinho com a mensagem pronta (número cadastrado em
  // "Dados da Loja"). O link também é devolvido pro OrderForm — se o
  // navegador bloquear essa abertura automática (acontece às vezes, é
  // comportamento do navegador, não tem como garantir 100%), o botão de
  // WhatsApp continua disponível na tela de sucesso como alternativa manual.
  // Salva o rascunho atual (mapa aplicado na UV + preview) — pode ser
  // chamado várias vezes enquanto o cliente ainda está decidindo o layout;
  // da segunda vez em diante atualiza o mesmo registro em vez de duplicar.
  // Também grava o id na URL (?save=), pra o cliente conseguir voltar pra
  // esse mesmo rascunho copiando/favoritando o link.
  const SVGS_BASE = "https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/svgs/"
  const IMAGES_BASE_URL = "https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/images/"

  type SavedDraft = { id: number; sport_id: string; svg: string; thumb: string; savedAt: string }

  function draftsStorageKey(ownerId: string) {
    return `iframeSavedDrafts_${ownerId}`
  }

  function readDraftsFromStorage(ownerId: string): SavedDraft[] {
    try {
      const raw = localStorage.getItem(draftsStorageKey(ownerId))
      return raw ? JSON.parse(raw) : []
    } catch {
      return []
    }
  }

  function writeDraftToStorage(ownerId: string, draft: SavedDraft) {
    const list = readDraftsFromStorage(ownerId).filter((d) => d.id !== draft.id)
    list.unshift(draft) // mais recente primeiro
    localStorage.setItem(draftsStorageKey(ownerId), JSON.stringify(list.slice(0, 20)))
  }

  async function handleSaveProgress() {
    (window as any)["setupCamera"]();
    setIsSavingProgress(true)

    setTimeout(async () => {
      try {
        const dataURL = (window as any).renderer?.domElement.toDataURL("image/png")
        const fabricCanvas = (window as any).fabricCanvas
        const svgString = fabricCanvas ? fabricCanvas.toSVG() : null
        const projectIframe = (window as any).project_iframe || {}
        const ownerId = (window as any).userIDIframe || projectIframe.user_id

        if (!ownerId || !dataURL || !svgString) {
          alert("Não foi possível salvar agora. Tente novamente em alguns segundos.")
          return
        }

        const saved = await saveIframeProgress(
          ownerId,
          projectIframe.sport_id,
          dataURL,
          svgString,
          savedProgressId
        )

        if (!saved) {
          alert("Não foi possível salvar. Tente novamente.")
          return
        }

        setSavedProgressId(saved.id)

        const url = new URL(window.location.href)
        url.searchParams.set("save", String(saved.id))
        window.history.replaceState({}, "", url.toString())

        // Guarda no localStorage desse navegador — é isso que alimenta o
        // menu "Minhas Artes Salvas", não precisa recarregar a página nem
        // depender do link continuar exatamente igual.
        writeDraftToStorage(ownerId, {
          id: saved.id,
          sport_id: saved.sport_id || projectIframe.sport_id,
          svg: saved.svg,
          thumb: saved.thumb,
          savedAt: new Date().toISOString(),
        })
        setSavedDrafts(readDraftsFromStorage(ownerId))
      } catch (e) {
        console.error("[handleSaveProgress] erro ao salvar progresso:", e)
        alert("Não foi possível salvar. Tente novamente.")
      } finally {
        setIsSavingProgress(false)
      }
    }, 100)
  }

  // Aplica uma arte salva direto (sem precisar recarregar a página) —
  // reaproveita o mesmo carregador de mapa (pathSVG + loadMap) que o
  // Loads.js já usa internamente pra aplicar SVG sobre o modelo atual.
  function handleLoadDraft(draft: SavedDraft) {
    if (typeof (window as any)["restoreProgressSVG"] !== "function") {
      alert("O carregador de mapas ainda não está pronto. Aguarde um instante e tente de novo.")
      return
    }
    (window as any)["restoreProgressSVG"](SVGS_BASE + draft.svg)
    setIsDraftsMenuOpen(false)
  }

  function handleRemoveDraft(ownerId: string, id: number) {
    const list = readDraftsFromStorage(ownerId).filter((d) => d.id !== id)
    localStorage.setItem(draftsStorageKey(ownerId), JSON.stringify(list))
    setSavedDrafts(list)
  }

  function toggleDraftsMenu() {
    const projectIframe = (window as any).project_iframe || {}
    const ownerId = (window as any).userIDIframe || projectIframe.user_id
    if (ownerId) setSavedDrafts(readDraftsFromStorage(ownerId))
    setIsDraftsMenuOpen((v) => !v)
  }

  async function handleOrderSubmit(result: any) {
    const orderId = await saveIframeProject(result)

    let whatsappUrl: string | null = null
    if (sellerWhatsapp) {
      const digits = sellerWhatsapp.replace(/\D/g, "")
      const phone = digits.startsWith("55") ? digits : `55${digits}`
      const msg = buildWhatsAppMessage(result, orderId)
      whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
      window.open(whatsappUrl, "_blank")
    } else {
      console.warn("[handleOrderSubmit] loja sem WhatsApp cadastrado.")
    }

    return { orderId, whatsappUrl }
  }

  // Shared tab content props
  const tabProps = {
    activeTab, colors, onColorChange: handleColorChange,
    name, number, onNameChange: setName, onNumberChange: setNumber,
    logo, onLogoChange: setLogo,
  }

  // Mobile equivalent of desktop's view_only panel removal (see
  // manageOptions() in Loads.js): a catalog view-only link keeps only the
  // "Modelos" tab (choosing a design); a single-project view-only link
  // hides the customization panel entirely.
  const mobileHidePanel = shareMode.viewOnly && !shareMode.shared
  const mobileExcludeTabs: TabId[] = shareMode.viewOnly && shareMode.shared
    ? ["colors", "personalize", "logo", "layers"]
    : []

  // Mobile QR upload — render only the uploader, no simulator
  if (isMobileUpload) {
    // Esconde o loading do simulador que já pode ter aparecido
    if (typeof document !== "undefined") {
      const el = document.getElementById("loadingNew")
      if (el) el.style.display = "none"
    }
    return <UploadLogoMobile sessionId={params.get("qr")!} />
  }

  return (
    <div data-iframe-root className="w-full h-[100dvh] !fixed z-[1000000000000000000000000000000] bg-background flex flex-col overflow-hidden">

      {/* Ambient Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-primary/3 rounded-full blur-[100px]" />
      </div>

      {/* ── Top stripe bar ────────────────────────────────────────────────── */}
      <div className="relative bg-gradient-to-r from-primary via-primary to-primary overflow-hidden shrink-0 iframe-panel-remove">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-gradient" />
        {/* diagonal stripes left/right */}
        {["-70deg", "70deg"].map((deg, i) => (
          <div key={i} className={`absolute ${i === 0 ? "left" : "right"}-0 top-0 bottom-0 w-40 overflow-hidden`}>
            <div className="absolute inset-0" style={{ background: `repeating-linear-gradient(${deg},transparent,transparent 8px,rgba(0,0,0,0.12) 8px,rgba(0,0,0,0.12) 16px)` }} />
          </div>
        ))}
        <div className="relative z-10 flex items-center justify-between px-6 py-2 text-primary-foreground">
          <div className="flex items-center gap-4 text-xs font-semibold">
            <a target="_blank" href="#" className="hover:opacity-80 transition-opacity" id="site-iframe">SITE</a>
            <button className="theme-iframe">MUDAR TEMA</button>
          </div>
          <div className=" items-center gap-6 mx-auto sm:mx-0 flex">
            <div className="flex items-center gap-2">
              <a target="_blank" href="#" className="w-7 h-7 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-all hover:scale-105" id="instagram-iframe">
                <Instagram className="w-3.5 h-3.5" />
              </a>
            </div>
            <div className="flex items-center gap-2 text-xs font-medium">
              <Phone className="w-3.5 h-3.5" />
              <span id="phone-store">(62) 9281-7127</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Header — DESKTOP ONLY, byte-identical to the reference version.
          Mounted only when !isMobile, so it never coexists in the DOM with
          the mobile header below (no duplicate #ids to worry about). ── */}
      {!isMobile && (
        <header className="relative z-20 border-b border-border/50 glass-strong sticky top-0 shrink-0 iframe-panel-remove">
          <div className="container px-4 py-3 flex items-center justify-between ml-0 mr-0 max-w-full">
            {/* Logo + title */}
            <div className="flex items-center gap-4">
              <div className="relative group">
                <div className="absolute inset-0 bg-primary/30 rounded-2xl blur-xl group-hover:blur-2xl transition-all opacity-60" />
                <div className="relative w-20 h-20 bg-gradient-to-br from-primary to-primary/80 rounded-2xl flex items-center justify-center shadow-lg bg-center bg-[percentage:85%] bg-no-repeat" id="logo-iframe" />
              </div>
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-3">
              {!shareMode.isApproval && (
                <>
                  <div className="flex items-center gap-6 mr-4">
                    <button onClick={() => setHelpOpen(true)} className="flex items-center gap-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors group">
                      <div className="w-9 h-9 border border-border rounded-xl flex items-center justify-center group-hover:border-primary/50 group-hover:bg-primary/5 transition-all">
                        <HelpCircle className="w-4 h-4" />
                      </div>
                      <span className="font-medium">Ajuda</span>
                    </button>
                    <button className="flex items-center gap-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors group" id="open-form-iframe">
                      <span className="font-medium">Abrir Formulário</span>
                      <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center shadow-lg glow-sm group-hover:scale-105 transition-all">
                        <PlusCircle className="w-4 h-4 text-primary-foreground" />
                      </div>
                    </button>
                  </div>

                  <Button id="reset-iframe3" variant="ghost" size="sm" onClick={handleReset} className="flex gap-2 hover:bg-destructive/10 hover:text-destructive">
                    <RotateCcw className="w-4 h-4" />
                    <span>Resetar</span>
                  </Button>

                  <Button id="clear-iframe3" variant="ghost" size="sm" className="flex gap-2 hover:bg-destructive/10 hover:text-destructive">
                    <BrushCleaningIcon className="w-4 h-4" />
                    <span>Limpar</span>
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    className="flex gap-2"
                    disabled={isSavingProgress}
                    onClick={handleSaveProgress}
                  >
                    <Save className="w-4 h-4" />
                    <span>{isSavingProgress ? "Salvando..." : "Salvar"}</span>
                  </Button>

                  <div style={{ position: "relative", display: "inline-block" }}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex gap-2"
                      onClick={toggleDraftsMenu}
                    >
                      <FolderOpen className="w-4 h-4" />
                      <span>Minhas Artes</span>
                    </Button>

                    {isDraftsMenuOpen && (
                      <DraftsMenu
                        drafts={savedDrafts}
                        imagesBase={IMAGES_BASE_URL}
                        onSelect={handleLoadDraft}
                        onRemove={(id) => {
                          const projectIframe = (window as any).project_iframe || {}
                          const ownerId = (window as any).userIDIframe || projectIframe.user_id
                          if (ownerId) handleRemoveDraft(ownerId, id)
                        }}
                        onClose={() => setIsDraftsMenuOpen(false)}
                      />
                    )}
                  </div>
                </>
              )}

              {shareMode.isApproval ? (
                <Button
                  className="bg-green-600 hover:bg-green-700 shadow-lg font-semibold"
                  onClick={() => setIsApprovalOpen(true)}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  <span>Aprovar</span>
                </Button>
              ) : (
                <>
                  {hasPoseSupport && (
                    <button
                      onClick={async () => {
                        if (poseActive) {
                          (window as any).stopPoseTracking?.()
                          setPoseActive(false)
                        } else {
                          const ok = await (window as any).startPoseTracking?.()
                          if (ok) setPoseActive(true)
                        }
                      }}
                      className={`flex items-center gap-2.5 text-sm rounded-xl transition-colors px-3 py-2 ${
                        poseActive
                          ? "text-green-400 hover:text-green-300 bg-green-500/10 border border-green-500/30"
                          : "text-muted-foreground hover:text-foreground hover:bg-white/5 border border-border"
                      }`}
                    >
                      <User className="w-4 h-4 shrink-0" />
                      <span className="font-medium">{poseActive ? "Sair do Provador" : "Provador"}</span>
                      {poseActive && <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />}
                    </button>
                  )}
                  <Button
                    className="bg-primary hover:bg-primary/90 shadow-lg glow-sm font-semibold budget-iframe"
                    onClick={openModalBudget}
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    <span>Orçamento</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        </header>
      )}

      {/* ── DESKTOP layout — 3-column, byte-identical to the reference. ── */}
      {!isMobile && (
        <div className="flex-1 flex flex-row relative z-10 overflow-hidden">

          {/* LEFT panel — modelagens + designs */}
          <div className="flex flex-col w-72 m-3 mr-0 gap-3 shrink-0 overflow-hidden" id="iframe-left-panel">
            <div className="glass rounded-2xl p-3 overflow-y-auto custom-scrollbar flex-none max-h-[45%] hidden" id="modelagens-section">
              <div className="items-center gap-2 mb-2 hidden">
                <div className="w-8 h-8 bg-gradient-to-br from-primary/20 to-primary/5 rounded-xl flex items-center justify-center border border-primary/20 shrink-0">
                  <Shirt className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-sm">Modelagens</h3>
                  <p className="text-xs text-muted-foreground">Escolha o estilo</p>
                </div>
              </div>
              <div id="modelagens-iframe" />
            </div>

            <div className="glass rounded-2xl p-3 overflow-y-auto custom-scrollbar flex-1 min-h-0">
              <div className="hidden items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-gradient-to-br from-primary/20 to-primary/5 rounded-xl flex items-center justify-center border border-primary/20 shrink-0">
                  <PaletteIcon className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground text-sm">Designs</h3>
                  <p className="text-xs text-muted-foreground">Escolha o design</p>
                </div>
              </div>
              <div id="designs-iframe" />
            </div>
          </div>

          {/* CENTER — 3D preview. NOTE: on mobile, this same #container-iframe
              is rendered further down (still a single instance overall,
              since isMobile is mutually exclusive with this branch). */}
          <div
            className="flex-1 flex flex-col items-center justify-center relative"
            id="container-iframe"
          />

          {/* RIGHT panel — customization tabs (landscape desktop only;
              portrait uses the totem bottom buttons + modal below) */}
          {!isTotem && (
            <div className="flex flex-col w-[360px] m-3 ml-0 shrink-0" id="iframe-right-panel">
              <div className="glass rounded-2xl overflow-hidden flex flex-col h-full max-h-full">
                <TabBar activeTab={activeTab} onSelect={id => { if (id !== "models") handleTabSelect(id) }} variant="desktop" exclude={["models"]} />
                <TabContent {...tabProps} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Floating controls — totem (portrait, desktop UA) only ── */}
      {isTotem && hasPoseSupport && !shareMode.isApproval && !poseActive && (
        <div
          className="fixed z-[100000001] flex items-center gap-3"
          style={{
            top: "43px",
            left: 0,
            right: 0,
            justifyContent: "center",
            padding: "0 16px",
            overflowX: "auto",
            scrollbarWidth: "none",
          }}
        >
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 text-sm rounded-full transition-all px-4 py-2.5 shadow-xl text-white/90 bg-white/10 border border-white/15 hover:bg-white/20 hover:text-white"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
              <path d="M3 3v5h5"/>
              <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/>
              <path d="M16 16h5v5"/>
            </svg>
            <span className="font-medium">Voltar</span>
          </button>

          <button
            onClick={async () => {
              if (poseActive) {
                (window as any).stopPoseTracking?.()
                setPoseActive(false)
              } else {
                const ok = await (window as any).startPoseTracking?.()
                if (ok) setPoseActive(true)
              }
            }}
            className="flex items-center gap-2 text-sm rounded-full transition-all px-4 py-2.5 shadow-xl text-white/90 bg-white/10 border border-white/15 hover:bg-white/20 hover:text-white"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a4 4 0 0 0-4 4v2a4 4 0 0 0 8 0V6a4 4 0 0 0-4-4z"/>
              <path d="M10 22v-4a2 2 0 0 1 2-2v0a2 2 0 0 1 2 2v4"/>
              <path d="M7 10H5a2 2 0 0 0-2 2v1c0 3.87 3.13 7 7 7h2c3.87 0 7-3.13 7-7v-1a2 2 0 0 0-2-2h-2"/>
            </svg>
            <span className="font-medium">Provador</span>
          </button>

          <button
            onClick={() => (window as any)["setupCamera"]?.()}
            className="flex items-center gap-2 text-sm rounded-full transition-all px-4 py-2.5 shadow-xl text-white/90 bg-white/10 border border-white/15 hover:bg-white/20 hover:text-white"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 2v4"/>
              <path d="M12 18v4"/>
              <path d="m4.93 4.93 2.83 2.83"/>
              <path d="m16.24 16.24 2.83 2.83"/>
              <path d="M2 12h4"/>
              <path d="M18 12h4"/>
              <path d="m4.93 19.07 2.83-2.83"/>
              <path d="m16.24 7.76 2.83-2.83"/>
            </svg>
            <span className="font-medium">Recentralizar</span>
          </button>

          <button
            onClick={openModalBudget}
            className="flex items-center gap-2 text-sm rounded-full transition-all px-4 py-2.5 shadow-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
          >
            <ShoppingCart className="w-4 h-4" />
            <span>Orçamento</span>
          </button>
        </div>
      )}

      {/* ── TOTEM customization UI — desktop portrait only. Floating touch
          buttons at the bottom; "Modelos" toggles a designs sheet, the edit
          buttons open a centered modal. Uses its own #totem-ui id — NOT
          #iframe-right-panel — so the jQuery hides in manageOptions()
          (view_only etc.) never remove/hide React-managed totem nodes (that
          caused "insertBefore ... not a child of this node" crashes).
          In view_only shares only "Modelos" is offered (no editing). ── */}
      {isTotem && !shareMode.isApproval && (
        <div id="totem-ui">
          {/* Floating bottom button bar — no outer box (borderless); buttons
              themselves are the touch targets. max-width keeps edge buttons
              from being cut off on narrow portrait windows; horizontal scroll
              covers the rare overflow. */}
          <div
            className="fixed left-1/2 -translate-x-1/2 z-[99990] flex items-center justify-center gap-2"
            style={{
              bottom: "24px",
              maxWidth: "calc(100vw - 24px)",
              overflowX: "auto",
              padding: "0 8px",
              scrollbarWidth: "none",
            }}
          >
            <button
              onClick={toggleTotemDesigns}
              className={`flex flex-col items-center justify-center gap-1 w-[104px] h-[88px] rounded-2xl text-sm font-semibold transition-all active:scale-95 ${
                totemDesignsOpen
                  ? "bg-primary text-primary-foreground shadow-lg"
                  : "text-foreground/80 hover:text-foreground hover:bg-white/10"
              }`}
            >
              <Shirt className="w-7 h-7" />
              <span>Modelos</span>
            </button>
            {!shareMode.viewOnly && ([
              { id: "colors" as TabId, label: "Cores", icon: <Palette className="w-7 h-7" /> },
              { id: "personalize" as TabId, label: "Nome", icon: <Hash className="w-7 h-7" /> },
              { id: "logo" as TabId, label: "Imagens", icon: <ImageIcon className="w-7 h-7" /> },
              { id: "layers" as TabId, label: "Estampas", icon: <Layers2Icon className="w-7 h-7" /> },
            ]).map(t => (
              <button
                key={t.id}
                onClick={() => { handleTabSelect(t.id); setTotemDrawerTab(t.id) }}
                className={`flex flex-col items-center justify-center gap-1 w-[104px] h-[88px] rounded-2xl text-sm font-semibold transition-all active:scale-95 ${
                  activeTab === t.id
                    ? "bg-primary text-primary-foreground shadow-lg"
                    : "text-foreground/80 hover:text-foreground hover:bg-white/10"
                }`}
              >
                {t.icon}
                <span>{t.label}</span>
              </button>
            ))}
          </div>

          {/* Close button for the designs sheet (sits just above the sheet) */}
          {totemDesignsOpen && (
            <button
              onClick={toggleTotemDesigns}
              className="fixed right-4 z-[100000001] w-14 h-14 flex items-center justify-center rounded-2xl text-2xl text-white bg-white/10 border border-white/20 hover:bg-white/20 transition-colors"
              style={{ bottom: "376px" }}
            >
              ✕
            </button>
          )}

          {/* Designs sheet — always mounted so #designs-iframe-mobile exists
              for the populators; visibility is toggled. No bottom padding:
              the sheet sits BEHIND the floating button bar, so cards use the
              full height. */}
          <div
            className="fixed bottom-0 left-0 right-0"
            style={{
              height: "360px",
              display: totemDesignsOpen ? "flex" : "none",
              flexDirection: "column",
              background: "linear-gradient(180deg, rgba(30,40,70,0.72) 0%, rgba(20,28,52,0.82) 100%)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              borderTop: "1px solid rgba(255,255,255,0.08)",
              zIndex: 100000000,
            }}
          >
            {/* Modelagens row — renderModels (simulator-selector.js) requires
                a #modelagens-iframe[-mobile] wrap; without it, it returns
                early and never renders the designs either (multi-model
                orders). Hidden while empty (CSS :has). */}
            <div id="modelagens-iframe-mobile" className="shrink-0" />
            <div id="designs-iframe-mobile" className="flex-1 min-h-0" />
          </div>

          {/* Editing modal */}
          {totemDrawerTab && (
            <div
              className="fixed inset-0"
              style={{ zIndex: 100000010 }}
              onClick={() => setTotemDrawerTab(null)}
            >
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
              <div
                id="totem-panel"
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-[560px] max-h-[78vh] glass-strong border border-border/60 rounded-3xl shadow-2xl flex flex-col overflow-hidden"
                onClick={e => e.stopPropagation()}
              >
                <div className="flex items-center justify-between gap-2 p-3 border-b border-border/50 shrink-0">
                  <h3 className="font-bold text-foreground text-lg px-3">Personalizar</h3>
                  <button
                    onClick={() => setTotemDrawerTab(null)}
                    className="w-14 h-14 flex items-center justify-center rounded-2xl text-2xl text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
                  >
                    ✕
                  </button>
                </div>

                <div className="flex shrink-0 p-2 gap-2 bg-secondary/30 rounded-2xl m-3 mb-0">
                  {([
                    { id: "colors" as TabId, label: "Cores", icon: <Palette className="w-5 h-5" /> },
                    { id: "personalize" as TabId, label: "Nome", icon: <Hash className="w-5 h-5" /> },
                    { id: "logo" as TabId, label: "Imagens", icon: <ImageIcon className="w-5 h-5" /> },
                    { id: "layers" as TabId, label: "Estampas", icon: <Layers2Icon className="w-5 h-5" /> },
                  ]).map(t => (
                    <button
                      key={t.id}
                      onClick={() => handleTabSelect(t.id)}
                      className={`flex-1 flex flex-col items-center justify-center gap-1 py-3 px-1 text-xs font-semibold rounded-xl transition-all ${
                        activeTab === t.id
                          ? "bg-primary text-primary-foreground shadow-lg"
                          : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                      }`}
                    >
                      {t.icon}
                      <span>{t.label}</span>
                    </button>
                  ))}
                </div>

                <TabContent {...tabProps} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── MOBILE layout — floating header + fullscreen-capable 3D view +
          always-visible customization panel below it. Completely separate
          tree from the desktop one above; only one of the two is ever
          mounted, so re-using the same #ids (logo-iframe, container-iframe,
          etc.) across both is safe. ── */}
      {isMobile && (
        <div className="flex-1 flex flex-col relative z-10 overflow-hidden">

          {/* CENTER — 3D preview. Fixed-height band (grows to fill when
              mobileFullscreen is on, or when there's no bottom panel to show
              at all — view-only/approval links with no category switching,
              where reserving space for an empty panel makes no sense). The
              floating header + fullscreen toggle are overlaid on top of it
              via absolute positioning. */}
          <div
            className={cn(
              "relative w-full overflow-hidden",
              (mobileFullscreen || mobileHidePanel || isTotem) ? "flex-1 min-h-0" : "h-[calc(42dvh+6rem)] min-h-[320px] max-h-[560px] shrink-0"
            )}
          >
            <div
              className="absolute inset-0 flex flex-col items-center justify-center"
              id="container-iframe"
            />

            {/* Floating header overlay — logo, help/actions menu, budget */}
            {!mobileFullscreen && (
              <header className="absolute top-0 inset-x-0 z-30 border-border/50 glass-strong iframe-panel-remove">
                <div className="px-4 py-3 flex items-center justify-between">
                  <div className="relative group">
                    <div className="absolute inset-0 bg-primary/30 rounded-2xl blur-xl opacity-60" />
                    <div className="relative w-14 h-14 bg-gradient-to-br from-primary to-primary/80 rounded-2xl flex items-center justify-center shadow-lg bg-center bg-[percentage:85%] bg-no-repeat" id="logo-iframe" />
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Actions dropdown (Ajuda, Abrir Formulário, Resetar, Limpar) —
                        oculto por completo no modo aprovação, só o botão
                        "Aprovar" deve aparecer nesse caso. */}
                    {!shareMode.isApproval && (
                    <div className="relative">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-9 h-9 p-0 flex items-center justify-center text-muted-foreground"
                        onClick={() => setInfoOpen(o => !o)}
                      >
                        <span className="text-lg leading-none tracking-widest">···</span>
                      </Button>

                      {infoOpen && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setInfoOpen(false)} />
                          <div
                            className="absolute right-0 top-full mt-2 z-50 border border-border/50 rounded-2xl p-2 w-52 shadow-xl flex flex-col gap-0.5"
                            style={{ backgroundColor: "#16161a" }}
                          >
                            <button onClick={() => { setHelpOpen(true); setInfoOpen(false) }} className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-white/5 rounded-xl transition-colors">
                              <HelpCircle className="w-4 h-4 shrink-0" />
                              <span className="font-medium">Ajuda</span>
                            </button>

                            <button id="open-form-iframe-mobile" className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-foreground hover:text-primary hover:bg-primary/5 rounded-xl transition-colors">
                              <PlusCircle className="w-4 h-4 shrink-0 text-primary" />
                              <span className="font-medium">Abrir Formulário</span>
                            </button>

                            <div className="h-px bg-border/50 my-1 mx-2" />

                            {hasARSupport && (
                            <button
                              id="ar-iframe"
                              onClick={() => { document.getElementById("arButton")?.click(); setInfoOpen(false) }}
                              className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-white/5 rounded-xl transition-colors"
                            >
                              <BoxesIcon className="w-4 h-4 shrink-0" />
                              <span className="font-medium">Realidade Aumentada</span>
                            </button>
                            )}

                            {hasPoseSupport && (
                            <button
                              onClick={async () => {
                                if (poseActive) {
                                  (window as any).stopPoseTracking?.()
                                  setPoseActive(false)
                                } else {
                                  const ok = await (window as any).startPoseTracking?.(isMobile ? "environment" : undefined)
                                  if (ok) setPoseActive(true)
                                }
                                setInfoOpen(false)
                              }}
                              className={`flex items-center gap-3 w-full px-3 py-2.5 text-sm rounded-xl transition-colors ${
                                poseActive
                                  ? "text-green-400 hover:text-green-300 bg-green-500/10"
                                  : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                              }`}
                            >
                              <User className="w-4 h-4 shrink-0" />
                              <span className="font-medium">{poseActive ? "Sair do Provador" : "Provador"}</span>
                              {poseActive && <span className="ml-auto w-2 h-2 rounded-full bg-green-400 animate-pulse" />}
                            </button>
                            )}

                            <button
                              onClick={() => { handleReset(); setInfoOpen(false) }}
                              className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/5 rounded-xl transition-colors"
                            >
                              <RotateCcw className="w-4 h-4 shrink-0" />
                              <span className="font-medium">Resetar</span>
                            </button>

                            <button
                              onClick={() => { handleReset(); setInfoOpen(false) }}
                              className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/5 rounded-xl transition-colors"
                            >
                              <BrushCleaningIcon className="w-4 h-4 shrink-0" />
                              <span className="font-medium">Limpar</span>
                            </button>

                            <button
                              disabled={isSavingProgress}
                              onClick={() => { handleSaveProgress(); setInfoOpen(false) }}
                              className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 rounded-xl transition-colors"
                            >
                              <Save className="w-4 h-4 shrink-0" />
                              <span className="font-medium">{isSavingProgress ? "Salvando..." : "Salvar"}</span>
                            </button>

                            <div style={{ position: "relative" }}>
                              <button
                                onClick={() => { toggleDraftsMenu() }}
                                className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 rounded-xl transition-colors"
                              >
                                <FolderOpen className="w-4 h-4 shrink-0" />
                                <span className="font-medium">Minhas Artes</span>
                              </button>
                              {isDraftsMenuOpen && (
                                <DraftsMenu
                                  drafts={savedDrafts}
                                  imagesBase={IMAGES_BASE_URL}
                                  onSelect={(d) => { handleLoadDraft(d); setInfoOpen(false) }}
                                  onRemove={(id) => {
                                    const projectIframe = (window as any).project_iframe || {}
                                    const ownerId = (window as any).userIDIframe || projectIframe.user_id
                                    if (ownerId) handleRemoveDraft(ownerId, id)
                                  }}
                                  onClose={() => setIsDraftsMenuOpen(false)}
                                />
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    )}

                    {shareMode.isApproval ? (
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 shadow-lg font-semibold"
                        onClick={() => setIsApprovalOpen(true)}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="bg-primary hover:bg-primary/90 shadow-lg glow-sm font-semibold budget-iframe"
                        onClick={openModalBudget}
                      >
                        <ShoppingCart className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </header>
            )}

          </div>

          {/* Fullscreen toggle — OUTSIDE the overflow-hidden preview div so
              it's never clipped. Always visible on mobile when there's a panel
              below. Shows Minimize2 when in fullscreen, Maximize2 otherwise.
              Hidden in totem mode (the 3D view already fills the screen). */}
          {!mobileHidePanel && !isTotem && (
            <button
              onClick={() => setMobileFullscreen(f => !f)}
              className="fixed bottom-20 left-3 z-[9999] w-12 h-12 rounded-2xl bg-black/60 backdrop-blur-md border border-white/20 flex items-center justify-center text-white shadow-lg hover:bg-black/80 active:scale-95 transition-all"
              aria-label={mobileFullscreen ? "Sair da tela cheia" : "Ver em tela cheia"}
            >
              {mobileFullscreen ? <Minimize2 className="w-6 h-6" /> : <Maximize2 className="w-6 h-6" />}
            </button>
          )}

          {/* Customization panel — always visible below the 3D view (no
              tap-to-open drawer). Hidden while in fullscreen, in totem mode
              (replaced by the bottom buttons + modal), or entirely for a
              single-project view_only link (mobileHidePanel). */}
          {!mobileFullscreen && !mobileHidePanel && !isTotem && (
            <div className="flex flex-col flex-1 min-h-0 glass-strong border-t border-border/50 overflow-hidden">
              <div className="flex flex-col flex-1 overflow-hidden px-3 pt-3 pb-3">
                {TABS.length - mobileExcludeTabs.length > 1 && (
                  <TabBar activeTab={activeTab} onSelect={handleTabSelect} variant="mobile" exclude={mobileExcludeTabs} />
                )}
                <TabContent {...tabProps} isMobile={true} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Budget Modal — agora reaproveita o mesmo formulário de 5 passos
          ("Tenho interesse") usado na landing page (Sections.jsx), em vez
          da antiga lista de vendedores/WhatsApp. */}
      {isModalOpen && budgetDesign && (
        <div className="jb-modal-overlay" style={{ zIndex: 2147483647 }}>
          <div className="jb-modal jb-modal--design-full jb-modal--with-form">
            <button className="jb-modal__close" onClick={() => setIsModalOpen(false)}>✕</button>
            <div className="jb-modal--split">
              <div className="jb-modal--split-left">
                <img
                  src={budgetDesign.thumbnail}
                  alt={budgetDesign.name}
                  className="jb-modal__design-fullimg"
                  style={{ maxHeight: "100%", objectFit: "contain" }}
                />
              </div>
              <div className="jb-modal--split-right">
                <h2 className="jb-form__header-title">Tenho interesse</h2>
                <OrderForm
                  design={budgetDesign}
                  onClose={() => setIsModalOpen(false)}
                  ownerId={(window as any).userIDIframe || (window as any).project_iframe?.user_id}
                  onSubmit={handleOrderSubmit}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {isApprovalOpen && (
        <ApprovalFlow
          ownerId={(window as any).userIDIframe || (window as any).project_iframe?.user_id}
          iframeOptionsId={new URLSearchParams(window.location.search).get("n") || undefined}
          projectId={(window as any).project_iframe?.id}
          companyName={companyInfo.name || undefined}
          companyEmail={companyInfo.email || undefined}
          clientName={shareMode.approvalClientName || "Cliente"}
          onClose={() => setIsApprovalOpen(false)}
        />
      )}

      {/* Help Modal */}
      <HelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  )
}